import { Component, ErrorInfo, PropsWithChildren, ReactNode } from 'react'

import DefaultFallback from './DefaultFallback'

type FallbackProps = {
  error: Error | null
  onReset: () => void
  componentStack: string | null
}

type ErrorBoundaryProps = PropsWithChildren<{
  fallback?: ReactNode | ((props: FallbackProps) => ReactNode)
  onReset?: () => void
}>

type ErrorBoundaryState = {
  hasError: boolean
  error: Error | null
  componentStack: string | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    componentStack: null,
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({
      hasError: true,
      error,
      componentStack: info?.componentStack ?? null,
    })
  }

  onReset = () => {
    this.setState({
      hasError: false,
      error: null,
      componentStack: null,
    })

    if (this.props.onReset) this.props.onReset()
  }

  render() {
    const { hasError, error, componentStack } = this.state
    const { onReset } = this
    const { fallback, children } = this.props

    if (!hasError) return children

    const renderProps = { error, onReset, componentStack }

    return (
      (typeof fallback === 'function' ? fallback(renderProps) : fallback) || (
        <DefaultFallback {...renderProps} />
      )
    )
  }
}

export default ErrorBoundary
